import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { CustomValidators } from '@core-lib/classes';
import { AppConstants } from '@core-lib/config';
import { compareDatesOnly } from '@core-lib/functions';
import { SharedStoreSelectors } from '@core-lib/store/shared-store';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
@Component({
	selector: 'date-picker-range',
	templateUrl: './date-picker-range.component.html',
	styleUrls: [],
	standalone: false
})
export class DatePickerRangeComponent implements OnInit {
	@Input() fromDateControl: FormControl<Date | null>;
	@Input() toDateControl: FormControl<Date | null>;
	@Input() fromDateLabel: string;
	@Input() toDateLabel: string;
	@Input() classCol: string;
	@Input() isRequiredFromDate: boolean;
	@Input() isRequiredToDate: boolean;
	@Input() minDateDebut?: Date; // Paramétrable depuis le parent
	fromDate: Date = undefined;
	toDate: Date = undefined;

	@ViewChild('pickerFromDate') pickerFromDate!: MatDatepicker<Date>;
	@ViewChild('pickerToDate') pickerToDate!: MatDatepicker<Date>;
	@ViewChild('fromDateInput', { read: ElementRef }) fromDateInput!: ElementRef<HTMLInputElement>;
	@ViewChild('toDateInput', { read: ElementRef }) toDateInput!: ElementRef<HTMLInputElement>;

	@HostListener('window:keydown.tab', ['$event'])

	onKeyDown() {
		const active = document.activeElement as HTMLElement | null;
		if (!active) return;

		if (active === this.fromDateInput?.nativeElement) {
			this.pickerFromDate?.close();
		}
		if (active === this.toDateInput?.nativeElement) {
			this.pickerToDate?.close();
		}
	}

	@HostListener('keydown.shift.tab', ['$event'])
	onKeyDownA() {
		const active = document.activeElement as HTMLElement | null;
		if (!active) return;

		if (active === this.fromDateInput?.nativeElement) {
			this.pickerFromDate?.close();
		}
		if (active === this.toDateInput?.nativeElement) {
			this.pickerToDate?.close();
		}
	}

	get maxDateDebut(): Date | null {
		if (!this.toDateControl?.value) return null;
		let date = new Date(this.toDateControl?.value);
		let datecompare = new Date(this.toDateControl?.value).setDate(new Date(this.toDateControl?.value).getDate() - 1);
		return this.toDateControl?.value && compareDatesOnly(this.toDateControl?.value, this.fromDateControl?.value) !== -1
			? this.getMaxDate(date, dayjs(datecompare).toDate())
			: dayjs(new Date()).startOf('day').toDate();
	}
	get minDateFin(): Date | null {
		return this.fromDateControl?.value ? dayjs(this.fromDateControl?.value).toDate() : null;
	}

	getMaxDate(toDateControlValue: Date | null, referenceDate: Date): Date {
		let result = toDateControlValue > referenceDate ? toDateControlValue : referenceDate;
		return result;
	}

	constructor(
		private dateAdapter: DateAdapter<any>,
		private store$: Store,
		private elementRef: ElementRef
	) {
		this.store$.select(SharedStoreSelectors.selectLanguage).subscribe(language => {
			this.dateAdapter.setLocale(language);
		});
	}

	ngOnInit(): void {
		this.fromDateControl.addValidators([CustomValidators.invalidDateValidator()]);
		this.toDateControl.addValidators([CustomValidators.invalidDateValidator()]);

		this.fromDateControl.valueChanges.subscribe(value => {
			if (value) {
				if (this.isRequiredToDate) {
					this.toDateControl.addValidators(Validators.required);
				}
				this.toDateControl.addValidators(CustomValidators.dateRangeToValidator(this.fromDateControl, this.toDateControl));
				this.toDateControl.updateValueAndValidity({ emitEvent: false });
				this.toDateControl.markAsDirty();
			} else {
				this.toDateControl.removeValidators(Validators.required);
				this.toDateControl.updateValueAndValidity({ emitEvent: false });
			}
		});
		this.toDateControl.valueChanges.subscribe(value => {
			if (value) {
				if (this.isRequiredFromDate) {
					this.fromDateControl.addValidators(Validators.required);
				}
				this.fromDateControl.addValidators(CustomValidators.dateRangeFromValidator(this.fromDateControl, this.toDateControl));
				this.fromDateControl.updateValueAndValidity({ emitEvent: false });
				this.fromDateControl.markAsDirty();
			} else {
				this.fromDateControl.removeValidators(Validators.required);
				this.fromDateControl.updateValueAndValidity({ emitEvent: false });
			}
		});

		// Si minDate n'est pas fourni, on ne bloque rien
		if (this.minDateDebut) {
			this.minDateDebut.setHours(0, 0, 0, 0); // Normalisation
		}
	}

	onValueChangeFrom(event: Date): void {
		if (event && event instanceof Date) {
			this.fromDateControl.setValue(event, { emitEvent: true });
			this.fromDate = event;
		}
	}

	onValueChangeTo(event: Date): void {
		if (event && event instanceof Date) {
			this.toDateControl.setValue(event, { emitEvent: true });
			this.toDate = event;
		}
	}

	onDateInputKeydown(event: KeyboardEvent, picker: MatDatepicker<Date>): void {
		if (!picker.opened) return;
		if (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete') {
			picker.close();
		}
	}

	onFromDateBlur(): void {
		const formDateInputValue = this.fromDateInput?.nativeElement?.value as string;
		if (!this.validateDateFormat(formDateInputValue)) {
			this.fromDateControl?.setErrors({ invalidDateError: true });
		}
	}

	onToDateBlur(): void {
		const toDateInputValue = this.toDateInput?.nativeElement?.value as string;
		if (!this.validateDateFormat(toDateInputValue)) {
			this.toDateControl?.setErrors({ invalidDateError: true });
		}
	}

	private validateDateFormat(dateString: string): boolean {
		return AppConstants.REGEX.datePickerRangeDateValidator.test(dateString);
	}
}

